import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Button from '../../components/ui/Button';
import { ArrowLeft, Mail, CheckCircle, AlertCircle } from 'lucide-react';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [view, setView] = useState('login'); // 'login' or 'forgot'
    const [success, setSuccess] = useState(false);
    const navigate = useNavigate();

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        const { error } = await supabase.auth.signInWithPassword({
            email,
            password,
        });

        if (error) {
            setError(error.message === 'Invalid login credentials' ? 'Email ou mot de passe incorrect.' : error.message);
            setLoading(false);
        } else {
            navigate('/admin');
        }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        // Envoyer l'email de récupération via le portail Vercel configuré dans Supabase
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: 'https://scbieu.vercel.app',
        });

        if (error) {
            setError(error.message);
            setLoading(false);
        } else {
            setSuccess(true);
            setLoading(false);
        }
    };

    if (view === 'forgot') {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center font-sans p-4">
                <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md border border-gray-100 relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-1.5 bg-[#EA723D]"></div>
                    
                    <button onClick={() => { setView('login'); setSuccess(false); setError(null); }} className="flex items-center gap-2 text-gray-400 hover:text-[#EA723D] transition-colors text-sm mb-8 font-bold uppercase tracking-wider">
                        <ArrowLeft size={16} /> Retour
                    </button>

                    <h1 className="text-2xl font-black text-[#2C353D] mb-2">Mot de passe oublié ?</h1>
                    <p className="text-gray-500 text-sm mb-8 leading-relaxed">
                        Entrez votre adresse email pour recevoir un lien de sécurisation de votre compte.
                    </p>

                    {success ? (
                        <div className="bg-green-50 border border-green-100 p-6 rounded-2xl text-center space-y-4">
                            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                                <CheckCircle className="text-green-600" size={24} />
                            </div>
                            <p className="text-green-800 text-sm font-medium">Lien envoyé !</p>
                            <p className="text-green-700 text-xs leading-relaxed">
                                Si l'adresse <strong>{email}</strong> correspond à un compte actif, vous recevrez un email d'ici quelques instants.
                            </p>
                            <p className="text-[10px] text-green-600/60 uppercase font-bold tracking-widest pt-2">
                                Pensez à vérifier vos spams
                            </p>
                        </div>
                    ) : (
                        <form onSubmit={handleForgotPassword} className="space-y-6">
                            <div>
                                <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2 ml-1">Email professionnel</label>
                                <div className="relative">
                                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={18} />
                                    <input
                                        required
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-200 rounded-xl focus:border-[#EA723D] focus:bg-white outline-none transition-all text-[#2C353D]"
                                        placeholder="votre@mail.fr"
                                    />
                                </div>
                            </div>

                            {error && (
                                <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 rounded-xl text-red-600 text-xs font-bold">
                                    <AlertCircle size={16} /> {error}
                                </div>
                            )}

                            <Button type="submit" className="w-full py-4 rounded-xl shadow-lg shadow-[#EA723D]/20 transition-transform active:scale-95" disabled={loading}>
                                {loading ? 'Envoi...' : 'Recevoir le lien'}
                            </Button>
                        </form>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center font-sans p-4">
            <div className="bg-white p-8 md:p-10 rounded-2xl shadow-xl w-full max-w-md border border-gray-100 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1.5 bg-[#2C353D]"></div>
                
                <h1 className="text-3xl font-black text-[#2C353D] mb-2 text-center">Espace Pro</h1>
                <p className="text-center text-gray-400 text-sm mb-10">Accédez à votre tableau de bord SCBI</p>
                
                <form onSubmit={handleLogin} className="space-y-6">
                    <div className="space-y-2">
                        <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Email</label>
                        <input
                            required
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl focus:border-[#2C353D] focus:bg-white outline-none transition-all text-[#2C353D]"
                            placeholder="votre@mail.fr"
                        />
                    </div>
                    <div className="space-y-2">
                        <div className="flex justify-between items-center ml-1">
                            <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400">Mot de passe</label>
                            <button type="button" onClick={() => setView('forgot')} className="text-[10px] font-bold text-[#EA723D] hover:underline uppercase tracking-widest">
                                Oublié ?
                            </button>
                        </div>
                        <input
                            required
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl focus:border-[#2C353D] focus:bg-white outline-none transition-all text-[#2C353D]"
                            placeholder="••••••••"
                        />
                    </div>

                    {error && (
                        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 rounded-xl text-red-600 text-xs font-bold text-center justify-center">
                            <AlertCircle size={16} /> {error}
                        </div>
                    )}

                    <Button type="submit" className="w-full py-4 text-sm font-bold uppercase tracking-widest rounded-xl bg-[#2C353D] hover:bg-[#1a1a1f] shadow-lg shadow-gray-200 transition-all active:scale-95" disabled={loading}>
                        {loading ? 'Vérification...' : 'Se connecter'}
                    </Button>
                </form>
            </div>
        </div>
    );
};

export default Login;
